import React, { useState } from 'react';
import { PARTNER_TYPES, QUALIFICATION_CRITERIA } from '../../data/partners'; 

const CapitalTenderPortal = () => {
    const [activeTab, setActiveTab] = useState('qualify');

    return (
        <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
            <header className="mb-6">
                <h2 className="text-2xl font-bold text-slate-900">💰 Capital Clearinghouse</h2>
                <p className="text-sm text-slate-600">The unbiased tender portal connecting Alumni projects with institutional capital.</p>
            </header>

            {/* TABS */}
            <div className="flex space-x-6 border-b border-slate-300 mb-6">
                <button 
                    onClick={() => setActiveTab('qualify')}
                    className={`pb-2 text-sm font-bold ${activeTab === 'qualify' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}
                >
                    For Institutions: Qualification Tender
                </button>
                <button 
                    onClick={() => setActiveTab('compare')}
                    className={`pb-2 text-sm font-bold ${activeTab === 'compare' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}
                >
                    For Alumni: Compare Bids (Unbiased)
                </button>
            </div>

            {/* TAB 1: QUALIFICATION FORM */}
            {activeTab === 'qualify' && (
                <div className="animate-fade-in">
                    <div className="bg-blue-50 p-4 rounded mb-6 border border-blue-100">
                        <h4 className="font-bold text-blue-900 text-sm mb-1">Instructions for Financial Partners</h4>
                        <p className="text-xs text-blue-800">
                            To ensure transparency, Aulice requires a standardized "Standing Offer" submission. 
                            Your data will be used to calculate the Effective Cost of Capital (ECC) for our Alumni.
                        </p>
                    </div>

                    <form className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-6 rounded shadow-sm">
                        <div className="col-span-2">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Institution Type</label>
                            <select className="w-full p-2 border rounded">
                                {PARTNER_TYPES.map(type => <option key={type.id} value={type.id}>{type.label}</option>)}
                            </select>
                        </div>

                        {/* Identity Fields */}
                        <div className="col-span-2 md:col-span-1">
                            <h4 className="font-bold text-slate-800 mb-3 border-b pb-1">Identity & Capacity</h4>
                            <div className="space-y-3">
                                {QUALIFICATION_CRITERIA.identity.map(field => (
                                    <div key={field.id}>
                                        <label className="block text-xs font-bold text-gray-500">{field.label}</label>
                                        <input type={field.type} placeholder={field.placeholder} className="w-full p-2 border rounded" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Economic Fields */}
                        <div className="col-span-2 md:col-span-1">
                            <h4 className="font-bold text-slate-800 mb-3 border-b pb-1">The "Cost of Money" Menu</h4>
                            <div className="space-y-3">
                                {QUALIFICATION_CRITERIA.economics.map(field => (
                                    <div key={field.id}>
                                        <label className="block text-xs font-bold text-gray-500">{field.label}</label>
                                        {field.type === 'select' ? (
                                            <select className="w-full p-2 border rounded">
                                                {field.options.map(opt => <option key={opt}>{opt}</option>)}
                                            </select>
                                        ) : (
                                            <input type={field.type} placeholder={field.placeholder} className="w-full p-2 border rounded" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button className="col-span-2 bg-blue-600 text-white py-3 rounded font-bold hover:bg-blue-700 transition">
                            Submit Qualification Tender
                        </button>
                    </form>
                </div>
            )}

            {/* TAB 2: UNBIASED COMPARISON */}
            {activeTab === 'compare' && (
                <div className="animate-fade-in">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-slate-800">Active Bids: Project "Neural-X" ($2M Ask)</h3>
                        <span className="text-xs bg-slate-200 px-2 py-1 rounded text-slate-600">Sorted by ECC (Lowest Cost)</span>
                    </div>

                    <div className="overflow-x-auto bg-white rounded shadow-sm border">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-100 text-slate-600">
                                <tr>
                                    <th className="p-3 border-b">Bidder</th>
                                    <th className="p-3 border-b">Type</th>
                                    <th className="p-3 border-b">Rate</th>
                                    <th className="p-3 border-b">Equity Ask</th>
                                    <th className="p-3 border-b">Speed</th>
                                    <th className="p-3 border-b bg-blue-50 text-blue-900">ECC %</th>
                                    <th className="p-3 border-b">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Mock Data based on your descriptions */}
                                <tr className="border-b hover:bg-slate-50">
                                    <td className="p-3 font-bold">Royal Bank</td>
                                    <td className="p-3"><span className="bg-gray-100 px-2 py-1 rounded text-xs">Traditional</span></td>
                                    <td className="p-3">Prime + 1.5%</td>
                                    <td className="p-3 text-green-600">None</td>
                                    <td className="p-3">60 Days</td>
                                    <td className="p-3 font-bold text-blue-800">8.2%</td>
                                    <td className="p-3"><button className="text-blue-600 underline">Review Term Sheet</button></td>
                                </tr>
                                <tr className="border-b hover:bg-slate-50">
                                    <td className="p-3 font-bold">BDC Growth</td>
                                    <td className="p-3"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Gov Backed</span></td>
                                    <td className="p-3">Base + 5%</td>
                                    <td className="p-3 text-green-600">None</td>
                                    <td className="p-3">45 Days</td>
                                    <td className="p-3 font-bold text-blue-800">10.1%</td>
                                    <td className="p-3"><button className="text-blue-600 underline">Review Term Sheet</button></td>
                                </tr>
                                <tr className="border-b hover:bg-slate-50">
                                    <td className="p-3 font-bold">IronCrest Capital</td>
                                    <td className="p-3"><span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">Private Debt</span></td>
                                    <td className="p-3">Fixed 12%</td>
                                    <td className="p-3 text-red-500">2% Warrants</td>
                                    <td className="p-3">14 Days</td>
                                    <td className="p-3 font-bold text-blue-800">16.5%</td>
                                    <td className="p-3"><button className="text-blue-600 underline">Review Term Sheet</button></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CapitalTenderPortal;